import { Box } from '@mantine/core';
import { Filter } from '@repo/common/components/Filter';
import { FilterOperator } from '@repo/common/components/Filter/types';
import MultiSelectorTarget from '@repo/common/components/MultiSelector/MultiSelectorTarget';
import { isNil } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import type { ISecodaEntity } from '../../../api';
import { useCheckEntityUpdatePermission } from '../../../utils/authorization/roles';
import { FilterOptionType } from '../../Filter';
import { FILTER_OPTIONS_CONFIG } from '../../Filter/constants';
import { parsePropertyField } from '../../Properties/utils';
import { useMultiSelectorTargetSelected } from './RelatedEntitiesSelector/hooks.tsx';

interface RelatedEntitiesRenderProps {
	entity: ISecodaEntity & { related_entities?: ISecodaEntity[] };
	field?: string;
	hideOnEmpty?: boolean;
	onChange?: (entityId: string) => (value: (string | boolean)[]) => void;
	forceReadOnly?: boolean;
	defaultOpened?: boolean;
}

export default function RelatedEntitiesRender({
	entity,
	field = 'related_entities',
	onChange,
	hideOnEmpty = false,
	forceReadOnly = false,
	defaultOpened = false,
}: RelatedEntitiesRenderProps) {
	const initialValue = useMemo(
		() => parsePropertyField(entity, field),
		[entity, field]
	);

	const writePermission =
		useCheckEntityUpdatePermission({ entity: entity }) && !forceReadOnly;

	const [selected, setSelected] = useState(initialValue ?? []);

	const handleChange = useCallback(
		(value: (string | boolean)[]) => {
			if (value !== selected) {
				setSelected(value as string[]);
				onChange?.(entity.id)(value);
			}
		},
		[onChange, entity.id, selected]
	);

	const multiSelectorTargetSelected = useMultiSelectorTargetSelected(
		entity,
		selected
	);

	const children = useMemo(
		() => (
			<MultiSelectorTarget
				selected={multiSelectorTargetSelected}
				iconType="react-node"
				property="entity"
				propertyLabel="resources"
				isMenuItemBadge={false}
				isViewerUser={false}
				permittedId={''}
				readOnly
				hideOnEmpty={hideOnEmpty}
				truncate
			/>
		),
		[multiSelectorTargetSelected, hideOnEmpty]
	);

	const value = useMemo(
		() => ({
			filterType: FilterOptionType.RELATED,
			operator: FilterOperator.Is,
			value: selected,
		}),
		[selected]
	);

	if (isNil(onChange)) {
		return children;
	}

	if (!writePermission) {
		return children;
	}

	return (
		<Box pos="relative">
			<Filter
				value={value}
				filterOption={FILTER_OPTIONS_CONFIG[FilterOptionType.RELATED]}
				onChange={(filterValue) => handleChange(filterValue?.value as string[])}
				initialOpened={defaultOpened}
			>
				{children}
			</Filter>
		</Box>
	);
}
